import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '@/contexts/AppContext';
import { Bell, AlertTriangle, AlertCircle, Info, Zap, Sun, Home, Cpu, Battery, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useNotifications, NotificationEvent, NotificationSeverity, NotificationType } from '@/hooks/useNotifications';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { formatDistanceToNow } from 'date-fns';

const severityConfig: Record<NotificationSeverity, { 
  icon: typeof AlertTriangle; 
  className: string;
  dotClassName: string;
  borderClass: string;
}> = {
  critical: {
    icon: AlertCircle,
    className: 'text-destructive',
    dotClassName: 'bg-destructive',
    borderClass: 'border-l-destructive',
  },
  warning: {
    icon: AlertTriangle,
    className: 'text-wt-amber-primary',
    dotClassName: 'bg-wt-amber-primary',
    borderClass: 'border-l-wt-amber-primary',
  },
  info: {
    icon: Info,
    className: 'text-wt-slate-blue',
    dotClassName: 'bg-wt-slate-blue',
    borderClass: 'border-l-wt-slate-blue',
  },
};

const typeIcons: Record<NotificationType, typeof Battery> = {
  battery: Battery,
  forecast: Sun,
  solar: Zap,
  load: Home,
  system: Cpu,
};

const METRIC_LABELS: Record<string, string> = {
  energy_remaining_kwh: 'Energy remaining',
  net_draw_kw: 'Net draw',
  time_to_reserve: 'Time to reserve',
  time_to_sunrise: 'Time to sunrise',
  wasted_today_kwh: 'Estimated not used today',
  wasted_power_w: 'Current curtailment',
  forecast_kwh: 'Forecast',
  load_kw: 'Current load',
  soc_pct: 'Battery level',
  curtailment_confidence: 'Curtailment confidence',
  affected_system_count: 'Systems affected',
};

/** Context keys that drive behavior/CTA rather than being displayed as metrics. */
const CONTEXT_CONTROL_KEYS = new Set([
  'cta_deeplink', 'cta_label', 'cta_action', 'short_message', 'issue_id',
  'provider', 'provider_label', 'mode', 'event_kind', 'property_name',
]);

/* Canon layout: Title, explanation, metrics block (label: value unit, mono), confidence, timestamp */
function formatContextMetric(key: string, value: unknown): string | null {
  if (value == null) return null;
  const k = key.toLowerCase();
  if (k.includes('kwh') || k.includes('energy')) return `${Number(value).toFixed(1)} kWh`;
  if (k.includes('kw') || k.includes('power')) return `${Number(value).toFixed(1)} kW`;
  if (k.includes('hour') || k.includes('time') || k.includes('min')) return String(value);
  return String(value);
}

function NotificationRow({
  notification,
  onMarkSeen,
  onNavigateAndClose,
}: {
  notification: NotificationEvent;
  onMarkSeen: (id: string) => void;
  onNavigateAndClose: (notification: NotificationEvent) => void;
}) {
  const severity = severityConfig[notification.severity];
  const SeverityIcon = severity.icon;
  const TypeIcon = typeIcons[notification.type];
  const isUnseen = !notification.seen_at;
  const ctx = notification.context || {};
  const confidence = typeof ctx.confidence === 'string' ? ctx.confidence : null;
  const ctaLabel = typeof ctx.cta_label === 'string' ? ctx.cta_label : null;
  const metricKeys = Object.keys(ctx).filter(
    (k) => k !== 'confidence' && !CONTEXT_CONTROL_KEYS.has(k) && ctx[k] != null && typeof ctx[k] !== 'object'
  );

  const handleClick = () => {
    if (isUnseen) {
      onMarkSeen(notification.id);
    }
    onNavigateAndClose(notification);
  };

  return (
    <button
      onClick={handleClick}
      className={cn(
        "w-full text-left px-4 py-3 border-b border-border/50 last:border-b-0 border-l-[3px]",
        severity.borderClass,
        "hover:bg-muted/50 transition-colors",
        isUnseen && "bg-muted/30"
      )}
    >
      <div className="flex items-start gap-3">
        <div className={cn("mt-0.5 shrink-0", severity.className)}>
          <SeverityIcon className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-1">
            <TypeIcon className="h-3 w-3 text-muted-foreground shrink-0" />
            <span className={cn(
              "text-sm font-semibold",
              isUnseen ? "text-foreground" : "text-muted-foreground"
            )}>
              {notification.title}
            </span>
          </div>
          <p className="text-sm text-muted-foreground leading-[1.5] mb-2">
            {notification.message}
          </p>
          {metricKeys.length > 0 && (
            <div className="flex flex-col gap-0.5 mb-2 font-mono text-[13px] text-muted-foreground">
              {metricKeys.map((key) => {
                const label = METRIC_LABELS[key] ?? key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
                const value = formatContextMetric(key, ctx[key]);
                if (!value) return null;
                return (
                  <span key={key}>
                    {label}: <span className="font-mono-numeric text-foreground">{value}</span>
                  </span>
                );
              })}
            </div>
          )}
          {confidence && (
            <p className="text-[11px] text-muted-foreground mb-1">
              Confidence: {String(confidence).charAt(0).toUpperCase() + String(confidence).slice(1)}
            </p>
          )}
          <p className="text-[11px] text-muted-foreground/80 meta">
            {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
          </p>
          {ctaLabel && (
            <span className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-wt-accent">
              {ctaLabel}
              <ArrowRight className="h-3 w-3" />
            </span>
          )}
        </div>
        {isUnseen && (
          <div className={cn("w-2 h-2 rounded-full shrink-0 mt-1.5", severity.dotClassName)} />
        )}
      </div>
    </button>
  );
}

export function NotificationBell() {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const { setPropertyScope, properties } = useApp();

  const {
    notifications,
    isLoading,
    markSeen,
    markAllSeen,
    isMarkingAllSeen,
    unseenCount,
    highestUnseenSeverity,
  } = useNotifications({ limit: 15 });

  const handleNavigateAndClose = (notification: NotificationEvent) => {
    setIsOpen(false);
    const prop = properties.find((p) => p.id === notification.property_id);
    setPropertyScope(notification.property_id, prop?.name || 'Property');
    // A deep-link CTA (e.g. reconnect provider credentials) overrides the default
    // property-dashboard navigation.
    const ctaDeeplink = notification.context?.cta_deeplink;
    if (typeof ctaDeeplink === 'string' && ctaDeeplink.startsWith('/')) {
      navigate(ctaDeeplink);
      return;
    }
    navigate(`/app/property/${notification.property_id}`);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Close on escape
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);

  const dotClassName = highestUnseenSeverity 
    ? severityConfig[highestUnseenSeverity].dotClassName 
    : null;

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "relative p-2 rounded-md transition-colors",
          "hover:bg-muted/50",
          isOpen && "bg-muted/50"
        )}
        aria-label={`Notifications${unseenCount > 0 ? ` (${unseenCount} unread)` : ''}`}
      >
        <Bell className="h-5 w-5 text-muted-foreground" />
        {dotClassName && (
          <span className={cn(
            "absolute top-1.5 right-1.5 w-2 h-2 rounded-full",
            dotClassName
          )} />
        )}
      </button>

      {isOpen && (
        <div
          ref={dropdownRef}
          className={cn(
            "absolute right-0 top-full mt-2 z-50",
            "w-80 max-h-[400px] bg-wt-bg-secondary border border-border rounded-lg shadow-lg",
            "flex flex-col"
          )}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-3 py-2 border-b border-border">
            <span className="text-sm font-medium">Notifications</span>
            {unseenCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => markAllSeen()}
                disabled={isMarkingAllSeen}
                className="h-7 text-xs text-muted-foreground hover:text-foreground"
              >
                Mark all read
              </Button>
            )}
          </div>

          {/* Content */}
          <ScrollArea className="flex-1 max-h-[320px]">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary border-t-transparent" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                <Bell className="h-8 w-8 mb-2 opacity-50" />
                <p className="text-sm">No notifications yet</p>
              </div>
            ) : (
              <div>
                {notifications.map((notification) => (
                  <NotificationRow
                    key={notification.id}
                    notification={notification}
                    onMarkSeen={markSeen}
                    onNavigateAndClose={handleNavigateAndClose}
                  />
                ))}
              </div>
            )}
          </ScrollArea>
        </div>
      )}
    </div>
  );
}
